/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-external-request-patterns */
// Ember imports
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { isEmpty } from '@ember/utils';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
// External imports
import config from 'linced-app/config/environment';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { alias } from '@ember/object/computed';

export default class ManageSettingsController extends Controller {
  // Constants
  learnerPasswordMinLength = 6;

  defaultPasswordMinLength = 14;

  Scopes = Scopes;

  Jurisdiction = Jurisdiction;

  // Services
  @service
  fetch;

  @service
  session;

  @service
  options;

  @service
  permissions;

  @alias('session.school.jurisdiction')
  jurisdiction;

  // Attributes
  // Password changing properties
  @tracked
  passwordSaving = false;

  @tracked
  passwordChanged = false;

  @tracked
  newPassword = '';

  @action
  setNewPassword(value) {
    this.newPassword = value;
  }

  @tracked
  confirmPassword = '';

  @action
  setConfirmPassword(value) {
    this.confirmPassword = value;
  }

  @tracked
  changePasswordError;

  @tracked
  oldPassword;

  // Computed
  // isMfaEnrolled checks if there is a user account
  // and whether the user has any MFA factors enabled
  @computed('model.user.isMfaEnrolled')
  get isMfaEnrolled() {
    return this.get('model.user.isMfaEnrolled') ?? false;
  }

  // canChangeMfa checks if an admin can manage MFA factors for the user
  // if the user has any MFA factors enabled
  @computed('session.scopes.[]', 'model.user.scopes.[]', 'isMfaEnrolled')
  get canManageMfa() {
    // Ruleset:
    // - Hero Admins can manage MFA for all users.
    // - Hero Managers can manage MFA for users of scopes equal and lower than their own.
    // - Tenant admins can manage MFA for staff, caregivers, and learners within their tenant.
    // - Deny all.

    if (!this.isMfaEnrolled) {
      // The user isn't enrolled in MFA, so there's nothing to manage.
      return false;
    }

    let userScope = this.model.user.get('scopes.firstObject');

    // Check the current user's permissions
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // Hero Managers and Admins can manage MFA for all users at or below
      // their level.
      return this.permissions.meet(userScope);
    }
    if (this.permissions.equal(Scopes.TENANT_ADMIN)) {
      // Tenant Admins can manage MFA for all users (within their tenant) below
      // their level.
      return this.permissions.exceed(userScope);
    }
    // By default, deny all.
    return false;
  }

  // canChangePassword checks if an admin can change password for the user
  @computed('session.scopes.[]', 'model.user.scopes.[]')
  get canChangePassword() {
    // Ruleset:
    // - Hero Admins can change passwords for all users.
    // - Hero Managers can change passwords for users of scopes lower than their own.
    // - Tenant admins can change passwords for learners only.
    // - Deny all.

    if (isEmpty(this.model.user)) {
      // No user record to manage password for
      return false;
    }

    let userScope = this.model.user.get('scopes.firstObject');

    // Check the current user's permissions
    if (this.permissions.meet(Scopes.LINCED_ADMIN)) {
      // Hero Admins can change passwords for all users at or below
      // their level.
      return this.permissions.meet(userScope);
    }
    if (this.permissions.meet(Scopes.LINCED_MANAGER)) {
      // Hero Managers can change passwords for all users below
      // their level.
      return this.permissions.exceed(userScope);
    }
    if (this.permissions.equal(Scopes.TENANT_ADMIN)) {
      // Tenant Admins can change passwords only for learners.
      return userScope === Scopes.LEARNER;
    }
    // By default, deny all.
    return false;
  }

  @computed('model.user.scopes.[]')
  get minLength() {
    if (
      this.model.user.scopes.includes('urn:linced:organisation:role:learner')
    ) {
      return this.learnerPasswordMinLength;
    }
    return this.defaultPasswordMinLength;
  }

  async doUpdatePasswordRequest(uri, opts) {
    try {
      const response = await fetch(uri, opts);

      if (!response.ok) {
        const responseJson = await response.json();
        let details = responseJson.message;
        const errMsg = `Error changing password. ${details}`;
        // Reset/clear error and fields
        this.changePasswordError = errMsg;
        // Update flags to say password was changed successfully
        this.passwordSaving = false;
        return;
      }

      this.passwordSaving = false;
      this.passwordChanged = true;
      this.changePasswordError = '';
      this.oldPassword = '';
      this.newPassword = '';
      this.confirmPassword = '';

      this.toaster.success('Your password was successfully updated!');
    } catch (err) {
      this.toaster.error(err);
    }
  }

  @action
  async changePassword() {
    let userId = this.model.person.userId;
    let newPassword = this.newPassword;
    let confirmPassword = this.confirmPassword;

    // Clear "password changed" flag early, in case we abort due to a client-side validation error.
    this.passwordChanged = false;

    if (isEmpty(userId)) {
      this.changePasswordError = `${this.model.person.firstName} does not appear to have a Hero user account.`;
      return;
    }

    if (newPassword !== confirmPassword || newPassword.length === 0) {
      this.changePasswordError =
        "Oops! The new password doesn't seem to match.";
      return;
    }

    this.passwordSaving = true;
    this.changePasswordError = '';

    let data = {
      userId,
      oldPassword: 'not provided', // Required, but not validated when setting other users' passwords.
      newPassword: this.newPassword,
    };

    const tokenWrapper = await this.session.getToken();
    let options = {
      body: JSON.stringify(data),
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenWrapper.token}`,
      },
    };

    await this.doUpdatePasswordRequest(
      `${config.APP.apiHost}/auth/v4/users/${userId}/:password`,
      options,
    );
  }

  reset() {
    this.passwordSaving = false;
    this.passwordChanged = false;
    this.newPassword = '';
    this.confirmPassword = '';
  }
}
